import { Link } from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import React, { createContext, useContext, useMemo } from "react";
import { useNavigate } from "react-router-dom";

import type { EntityClickHandler } from "../../types/entityRegistry";
import { entityPath } from "../../utils/entityAddress";

interface EntityNavigation {
  // Opens an entity where the view is shown: in the overlay of the page
  // around it. Without it a link leads to the entity's full page.
  onEntityClick?: EntityClickHandler;
}

const EntityNavigationContext = createContext<EntityNavigation>({});

interface EntityNavigationProviderProps extends EntityNavigation {
  children: React.ReactNode;
}

/**
 * Tells every entity link beneath it how an entity is opened.
 * A detail view wraps its body in it and passes the handler it was given.
 */
export const EntityNavigationProvider: React.FC<
  EntityNavigationProviderProps
> = ({ onEntityClick, children }) => {
  const navigation = useMemo(() => ({ onEntityClick }), [onEntityClick]);
  return (
    <EntityNavigationContext.Provider value={navigation}>
      {children}
    </EntityNavigationContext.Provider>
  );
};

/**
 * Opens an entity as a link does: where the view around it is shown, else
 * on the entity's full page. For a control that is no link, such as a point
 * of a map.
 */
export const useOpenEntity = (): ((id: string, type: string) => void) => {
  const { onEntityClick } = useContext(EntityNavigationContext);
  const navigate = useNavigate();
  return (id, type) => {
    if (onEntityClick) {
      onEntityClick(id, type);
    } else {
      navigate(entityPath(id, type));
    }
  };
};

export interface EntityLinkProps {
  // The id of the entity
  id: string;
  // Its singular type key, as the response names it
  type: string;
  // Its name; null for an entity no source names
  name: string | null;
  sx?: SxProps<Theme>;
}

// What a link reads when the entity has no name
export const entityLinkText = (type: string, name: string | null): string =>
  name ?? `Unnamed ${type.replace(/_/g, " ")}`;

/**
 * The name of an entity as a link that carries its id and its type.
 *
 * The address of the link is the entity's full page, so that it opens in a
 * tab of its own like any link. A plain click opens the entity where the
 * view around it is shown.
 */
const EntityLink: React.FC<EntityLinkProps> = ({ id, type, name, sx }) => {
  const openEntity = useOpenEntity();
  const href = entityPath(id, type);

  const handleClick = (event: React.MouseEvent) => {
    // A click that asks for a new tab or window is the browser's
    if (
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }
    event.preventDefault();
    openEntity(id, type);
  };

  return (
    <Link
      href={href}
      onClick={handleClick}
      underline="hover"
      sx={{ overflowWrap: "anywhere", ...sx } as SxProps<Theme>}
    >
      {entityLinkText(type, name)}
    </Link>
  );
};

export default EntityLink;
